import React from 'react';
import {
  useNetSuiteEligibilityDetailsQuery,
  useSyncOrderToNetSuiteMutation,
  NetSuiteEligibilityDetailsDocument
} from 'generated/graphql';

export interface IMissingProduct {
  sku: string;
  name: string;
  productId: string;
}

export interface IMissingDiscountItem {
  discountItemId: string;
  discountType: string;
  rate: string;
}

export interface INetSuiteEligibilityDetails {
  orderId: string;
  orderNumber: string;

  // Customer details
  customerInNetSuite: boolean;
  customerEmail: string | null;
  customerMissingReason: string | null;

  // Product details
  allProductsInNetSuite: boolean;
  missingProducts: IMissingProduct[];

  // Shipping service details
  hasShippingService: boolean;
  requestedShippingService: string | null;
  shippingServiceMissingReason: string | null;

  // Store configuration details
  storeHasDepartment: boolean;
  storeHasLocation: boolean;
  storeHasTaxItem: boolean;
  storeMissingReason: string | null;

  // Payment method details
  hasMappedPaymentMethod: boolean;
  rawPaymentMethod: string | null;
  paymentMethodMissingReason: string | null;

  // Discount items details
  discountItemsSynced: boolean;
  missingDiscountItems: IMissingDiscountItem[];

  // Write request status
  writeRequestGenerated: boolean;
  orderInNetSuite: boolean;
}

export interface IValidationError {
  id: string;
  writeRequestId: string;
  errors: string[];
}

export interface IValidationErrors {
  orderWriteRequestErrors: IValidationError | null;
  customerWriteRequestErrors: IValidationError | null;
}

export interface IData {
  eligibilityDetails: INetSuiteEligibilityDetails | null;
  validationErrors: IValidationErrors | null;
}

export function useQuery(orderId: string) {
  const queryTuple = useNetSuiteEligibilityDetailsQuery({
    variables: {
      orderId
    },
    errorPolicy: 'all' // Allow partial data if there are non-critical errors
  });

  return React.useMemo(
    () => {
      const eligibilityDetails = queryTuple.data?.netSuiteEligibilityDetails ? {
        orderId: queryTuple.data.netSuiteEligibilityDetails.orderId,
        orderNumber: queryTuple.data.netSuiteEligibilityDetails.orderNumber,

        // Customer details
        customerInNetSuite: queryTuple.data.netSuiteEligibilityDetails.customerInNetSuite,
        customerEmail: queryTuple.data.netSuiteEligibilityDetails.customerEmail ?? null,
        customerMissingReason: queryTuple.data.netSuiteEligibilityDetails.customerMissingReason ?? null,

        // Product details
        allProductsInNetSuite: queryTuple.data.netSuiteEligibilityDetails.allProductsInNetSuite,
        missingProducts: queryTuple.data.netSuiteEligibilityDetails.missingProducts || [],

        // Shipping service details
        hasShippingService: queryTuple.data.netSuiteEligibilityDetails.hasShippingService,
        requestedShippingService: queryTuple.data.netSuiteEligibilityDetails.requestedShippingService ?? null,
        shippingServiceMissingReason: queryTuple.data.netSuiteEligibilityDetails.shippingServiceMissingReason ?? null,

        // Store configuration details
        storeHasDepartment: queryTuple.data.netSuiteEligibilityDetails.storeHasDepartment,
        storeHasLocation: queryTuple.data.netSuiteEligibilityDetails.storeHasLocation,
        storeHasTaxItem: queryTuple.data.netSuiteEligibilityDetails.storeHasTaxItem,
        storeMissingReason: queryTuple.data.netSuiteEligibilityDetails.storeMissingReason ?? null,

        // Payment method details
        hasMappedPaymentMethod: queryTuple.data.netSuiteEligibilityDetails.hasMappedPaymentMethod,
        rawPaymentMethod: queryTuple.data.netSuiteEligibilityDetails.rawPaymentMethod ?? null,
        paymentMethodMissingReason: queryTuple.data.netSuiteEligibilityDetails.paymentMethodMissingReason ?? null,

        // Discount items details
        discountItemsSynced: queryTuple.data.netSuiteEligibilityDetails.discountItemsSynced,
        missingDiscountItems: queryTuple.data.netSuiteEligibilityDetails.missingDiscountItems || [],

        // Write request status
        writeRequestGenerated: queryTuple.data.netSuiteEligibilityDetails.writeRequestGenerated,
        orderInNetSuite: queryTuple.data.netSuiteEligibilityDetails.orderInNetSuite
      } : null;

      // Extract validation errors from order and customer write requests
      const orderWriteRequest = queryTuple.data?.order?.netSuiteWriteRequest;
      const customerWriteRequest = queryTuple.data?.order?.customer?.netSuiteWriteRequest;

      const validationErrors: IValidationErrors = {
        orderWriteRequestErrors: orderWriteRequest && orderWriteRequest.validationErrors && orderWriteRequest.validationErrors.length > 0
          ? {
            id: orderWriteRequest.id,
            writeRequestId: orderWriteRequest.id,
            errors: orderWriteRequest.validationErrors
          }
          : null,
        customerWriteRequestErrors: customerWriteRequest && customerWriteRequest.validationErrors && customerWriteRequest.validationErrors.length > 0
          ? {
            id: customerWriteRequest.id,
            writeRequestId: customerWriteRequest.id,
            errors: customerWriteRequest.validationErrors
          }
          : null
      };

      const data: IData = {
        eligibilityDetails,
        validationErrors: (validationErrors.orderWriteRequestErrors || validationErrors.customerWriteRequestErrors)
          ? validationErrors
          : null
      };

      return {
        ...queryTuple,
        data
      };
    },
    [queryTuple]
  );
}

export function useSyncOrderToNetSuite(orderId: string) {
  const [fnMutate, tuple] = useSyncOrderToNetSuiteMutation({
    refetchQueries: [
      {
        query: NetSuiteEligibilityDetailsDocument,
        variables: { orderId }
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    () => {
      fnMutate({
        variables: {
          orderId
        }
      });
    },
    [fnMutate, orderId]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
